import { Col, DatePicker, Form, Row, Select } from "antd";
import Table from "atoms/Table";
import Title from "atoms/Title";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import queryKeys from "common/queryKey";
import { useScrollFetch } from "hooks/useScrollFetch";
import { values } from "lodash";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { setFilter } from "redux/actions";
import {
  getClientid,
  getFilters,
  getManagingOffice,
  getUserInfo,
  getUserList,
} from "redux/selectors";
import { ButtonOutlined } from "reusableComponent/UIButtons";
import styled from "styled-components";
import { utils, writeFileXLSX } from "xlsx";


const RoundedSelect = styled(Select)`
  &:where(.ant-select) .ant-select-selector {
    border-radius: 15px !important;
  }

  &:where(.ant-select-focused) .ant-select-selector {
    border-radius: 15px !important;
  }

  &:where(.ant-select) .ant-select-selector:hover {
    border-radius: 15px !important;
  }
`;

function IndependenceAnalysis() {
  const userList = useSelector((state) => getUserList(state));
  const [payload, setPayload] = useState({});
  const clientId = useSelector(getClientid);
  const managingOfficeList = useSelector(getManagingOffice);
  const history = useHistory();
  const userInfo = useSelector(getUserInfo);
  const finduser = userList?.find((item) => item?.userid === userInfo?.userid);

  const isSuperAdmin = userInfo?.roleId === "2";

  const activeUsers = useMemo(() => {
    return userList?.filter(user => user?.statusid !== "0");
  }, [userList]);

  const columns = [
    {
      title: "Name",
      dataIndex: "fullname",
      key: "fullname",
      className: "normal-column ",
      render: (val) => {
        return <span className="text-primary">{val}</span>;
      },
      onCell: (record) => ({
        onClick: () => {
          if (record?.IndependenceForm) {
            history.push(`/independence-analysis/${record.userid}`);
          }
        },
        style: { cursor: "pointer" },
      }),
    },
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
      className: "normal-column",
      render: (val) => {
        return <span>{val ? val : "-"}</span>;
      },
    },

    {
      title: "Designation",
      dataIndex: "designation",
      key: "designation",
      className: "normal-column",
      ellipsis: {
        showTitle: true,
      },
      render: (val) => {
        return <span>{val === "null" || val === "" ? "-" : val}</span>;
      }
    },
    {
      title: "Email",
      dataIndex: "userid",
      key: "person_email_address",
      className: "normal-column",
      render: (val) => {
        return <span>{val === "null" || val === "" ? "-" : val}</span>;
      },
    },
    {
      title: "Submission Status",
      dataIndex: "independenceform",
      key: "independenceform",
      render: val => (
        <span>{val === "yes" ? "Yes" : "No"}</span>
      ),
    },
    {
      title: "Date Submitted",
      dataIndex: "time",
      key: "declarationSubmittedDate",
      className: "normal-column",
      render: (val) => {
        return <span>{val && val !== undefined && val !== null && val !== "null" ? moment(val).format("DD-MM-YYYY") : "-"}</span>;
      }
    },
  ];

  if (finduser?.managingoffice[0] === "Head Office") {
    columns.push({
      title: "Managing Office",
      dataIndex: "managingoffice",
      key: "managingoffice",
      className: "normal-column",
      render: (val, record) => {
        return (
          <span>
            {record?.managingoffice ? record?.managingoffice : "-"}
          </span>
        );
      },
    });
  }

  const [form] = Form.useForm();

  const filters = useSelector(getFilters);
  const menuName = "alldeclaration";
  const initialFilter = filters[menuName];

  const dispatch = useDispatch();

  const handleFormChange = () => {
    const values = form.getFieldsValue();
    dispatch(setFilter(menuName, "name", values.name || []));
    // dispatch(
    //   setFilter(menuName, "managingoffice", values.managingoffice || [])
    // );
    dispatch(setFilter(menuName, "status", values.status || []));
    dispatch(
      setFilter(menuName, "managingoffice", values.managingoffice || null)
    );
  };

  const { getAllDeclaration } = queryKeys;

  const selectedManagingOffice = isSuperAdmin
    ? filters?.alldeclaration?.managingoffice ||
    finduser?.managingoffice?.[0] ||
    managingOfficeList?.[0]
    : finduser?.managingoffice?.[0];

  useEffect(() => {
    if (
      isSuperAdmin &&
      !filters?.alldeclaration?.managingoffice &&
      finduser?.managingoffice?.[0]
    ) {
      const defaultOffice = finduser.managingoffice[0];

      dispatch(setFilter(menuName, "managingoffice", defaultOffice));

      form.setFieldsValue({
        managingoffice: defaultOffice,
      });
    }
  }, [isSuperAdmin, finduser]);

  const isHeadOffice =
    selectedManagingOffice === "Head Office";

  const { key, url } = getAllDeclaration(
    clientId,
    selectedManagingOffice
  );

  const { data, lastElementRef, isLoading, refetch, isReloading } =
    useScrollFetch(key, {
      endPoint: url,
      queryParam: "LastEvaluatedKey",
      extra: {
        userid: filters?.alldeclaration?.name ?? [],
        managingoffice: selectedManagingOffice
      },
      enabled: true,
    });

  const dataList = useMemo(() => {
      if (!data || !activeUsers?.length) return [];
      const activeUserIds = new Set(
        activeUsers.map(u => u.userid)
      );
      return data.pages.reduce((acc, page) => {
        if (!page?.response?.data) return acc;
          const filtered = page.response.data.filter(item =>
          activeUserIds.has(item.userid) &&
          item?.managingoffice === selectedManagingOffice
        );
        return [...acc, ...filtered];
      }, []);
    }, [data, activeUsers]);


  const userOptions = (
    finduser?.managingoffice[0] === "Head Office"
      ? activeUsers
      : activeUsers?.filter(user => user?.managingoffice[0] === finduser?.managingoffice[0])
  )?.map((user) => ({
    label: user?.fullname,
    value: user?.userid,
  }));

  const managingOfficeOption = managingOfficeList?.map((val) => {
    return {
      label: val,
      value: val,
    };
  });

  const statusOptions = [
    {
      label: "Submitted",
      value: "yes",
    },
    {
      label: "Not Submitted",
      value: "no",
    },
  ];

  const submittedUserIds = useMemo(() => {
    return new Set(
      dataList?.map(item => item?.userid)
    );
  }, [dataList]);

  const notSubmittedUsers = useMemo(() => {
     if (!activeUsers || activeUsers?.length === 0) return [];
    const selectedNames = form.getFieldValue("name") || [];

    return activeUsers
      .filter(user => {
        // Managing office check
        if (user?.managingoffice?.[0] !== selectedManagingOffice) {
          return false;
        }

        // 🔥 IMPORTANT FIX
        if (selectedNames?.length > 0 && !selectedNames?.includes(user?.userid)) {
          return false;
        }

        // NOT SUBMITTED
        return !submittedUserIds?.has(user?.userid);
      })
      .map(user => ({
        userid: user?.userid,
        fullname: user?.fullname,
        designation: user?.designation,
        managingoffice: user?.managingoffice?.[0],
        year: moment().format("YYYY"),
        independenceform: "no",
        time: null,
      }));
  }, [
    activeUsers,
    submittedUserIds,
    selectedManagingOffice,
    isHeadOffice,
    form, // ✅ needed
  ]);

  const uniqueByUserYear = (arr) => {
    const map = new Map();
    arr.forEach(item => {
      const key = `${item.userid}-${item.year}`;
      map.set(key, item);
    });
    return Array.from(map.values());
  };

  const getFilteredData = () => {
    const name = form.getFieldValue("name") || [];
    const status = form.getFieldValue("status") || [];
    const year = form.getFieldValue("year");

    let result = [];

    // 1️⃣ Combine YES + NO (base data)
    result = [...dataList, ...notSubmittedUsers];

    // 2️⃣ Name filter
    if (name?.length > 0) {
      result = result?.filter(item =>
        name?.includes(item?.userid)
      );
    }

    // 3️⃣ Status filter 🔥 FIX
    if (status?.length > 0) {
      result = result?.filter(item =>
        status?.includes(item?.independenceform)
      );
    }

    // 4️⃣ Year filter
    if (year && year?._d) {
      const selectedYear = moment(year?._d).format("YYYY");
      result = result?.filter(item =>
        String(item?.year) === selectedYear
      );
    }

    // 5️⃣ Deduplicate
    return uniqueByUserYear(result);
  };
  
  const xport = React.useCallback(() => {
    const elt = document.getElementById("Table");
    const wb = utils.table_to_book(elt);
    writeFileXLSX(wb, "Annual Declaration Report.csv");
  });

  return (
    <div>
      <Row>
        <Col sm={24}>
          <div className="d-flex justify-content-between">
            <Title>Annual Declaration</Title>
            <ButtonOutlined className="text-end" disabled={getFilteredData()?.length === 0 || null || undefined} onClick={xport}>
              Export CSV file
            </ButtonOutlined>
          </div>
        </Col>
        <Col sm={24}>
          <Form
            form={form}
            layout="vertical"
            className="mt-2"
            onValuesChange={handleFormChange}
            initialValues={{ ...initialFilter }}
          >
            <Row gutter={12} className="g-3 mb-2">
              <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                <DropdownCheckBox
                  name="name"
                  label="Name"
                  options={userOptions}
                  form={form}
                  handleFormChange={handleFormChange}
                />
              </Col>
              {/* {
                finduser?.managingoffice[0] === "Head Office" ? 
                <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                <DropdownCheckBox
                  name="managingoffice"
                  label="Managing office"
                  options={managingOfficeOption}
                  form={form}
                  handleFormChange={handleFormChange}
                />
              </Col> 
              : ""
              } */}
              <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                <DropdownCheckBox
                  name="status"
                  label="Status"
                  options={statusOptions}
                  form={form}
                  handleFormChange={handleFormChange}
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                <Form.Item name="year">
                  <DatePicker
                    style={{
                      border: "1px solid #ccc",
                      borderRadius: "50px",
                      width: "100%",
                    }}
                    picker="year"
                    format={"YYYY"}
                    placeholder="Year"
                  />
                </Form.Item>
              </Col>
              {isSuperAdmin && (
                <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                  <Form.Item name="managingoffice">
                    <RoundedSelect
                      placeholder="Select managing office"
                      options={managingOfficeOption}
                      clearIcon={null}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Form>
        </Col>
      </Row>
      <Table
        dataSource={getFilteredData()}
        columns={columns}
        pagination={false}
        loading={isLoading}
        onRow={(record, index) => ({
          ref: index === dataList?.length - 5 ? lastElementRef : null,
        })}
        id="Table"
      />
    </div>
  );
}

export default IndependenceAnalysis;
